
import type { PropType } from 'vue'

import type { Route } from 'vue-router'
import type { AppMessages, FeatureConfig, FeatureNode, FeaturesConfig } from '../models'
import { DomainEnum } from '@empathybroker/tsclient-play/dist/enums/domain.enum'

import { computed, defineComponent, getCurrentInstance } from 'vue'

import { mapGetters, useFieldValues } from '../composables'
import { CONTAINER } from '../config/inversify.config'
import { TYPES } from '../config/inversify.types'

import { CrudActionEnum, InstanceManagementFeatureIdEnum } from '../enums'

import { FeatureIdEnum } from '../enums/feature-id.enum'
import { ROUTER_CONSTS } from '../router/router.consts'

import { isExplainRoute } from '../router/routes/explain.routes'

import { isRelatedPromptsRoute } from '../router/routes/related-prompts.routes'
import { APP_CONSTS } from '../store/app/app.consts'
import { CONTEXT_CONSTS } from '../store/context/context.consts'
import { ArrayUtils } from '../utils/array.utils'
import { FeaturesTreeUtils } from '../utils/features-tree.utils'

export interface Breadcrumb {
  id: string
  label: string
  routeName?: string
}

export default defineComponent({
  name: 'BreadcrumbList',
  props: {
    messages: {
      type: Object as PropType<AppMessages>,
      required: true,
    },
  },
  setup(props: { messages: AppMessages }) {
    const baseClass = 'epb-breadcrumb-list'
    const messages = props.messages

    const ctx = getCurrentInstance()

    const { context } = mapGetters(ctx.proxy.$store, CONTEXT_CONSTS.moduleName, {
      context: CONTEXT_CONSTS.getters.context,
    })

    const { featuresTree } = mapGetters(ctx.proxy.$store, APP_CONSTS.moduleName, {
      featuresTree: APP_CONSTS.getters.featuresTree,
    })

    const { fieldValues: attributeValues } = useFieldValues({ reset: false })

    const getFeatureNode = (route: Route, domain: DomainEnum = DomainEnum.GLOBAL): FeatureNode => {
      const featureNode =
        (route.meta?.featureId?.includes(InstanceManagementFeatureIdEnum.INSTANCE_MANAGEMENT) &&
          route.name !== ROUTER_CONSTS.routes[FeatureIdEnum.INSTANCE_MANAGEMENT].root) ||
        !route.meta?.featureId?.includes(InstanceManagementFeatureIdEnum.INSTANCE_MANAGEMENT)
          ? FeaturesTreeUtils.getFeatureNode(route.meta?.featureId, featuresTree.value[domain])
          : undefined

      if (featureNode && route.meta) {
        if (
          Object.values(CrudActionEnum).some((action: CrudActionEnum) =>
            route.name?.includes(action),
          ) ||
          isExplainRoute(route) ||
          (isRelatedPromptsRoute(route) && route.name !== FeatureIdEnum.RELATED_PROMPTS) ||
          route.name === ROUTER_CONSTS.routes[FeatureIdEnum.CONFIGURATION][DomainEnum.BROWSE]?.list
        ) {
          return {
            ...featureNode,
            id: route.name,
            parentId: featureNode.id,
            routeName: route.name,
          }
        } else {
          return {
            ...featureNode,
            id: route.name,
            routeName: featureNode.routeName,
          }
        }
      }
      return featureNode
    }

    const getRouteLabel = (featureNode: FeatureNode, domain: DomainEnum): string => {
      let featureId = featureNode.id

      const action = Object.values(CrudActionEnum).find((action: CrudActionEnum) =>
        featureId?.includes(action),
      )
      const explainSection = Object.values(ROUTER_CONSTS.routes[FeatureIdEnum.EXPLAIN]).find(
        (routeName: string) => featureId === routeName,
      )
      const relatedPromptsSection = Object.values(
        ROUTER_CONSTS.routes[FeatureIdEnum.RELATED_PROMPTS],
      ).find((routeName: string) => featureId === routeName)

      if (action) {
        return messages.routes[action]
      }
      if (explainSection) {
        return messages.routes[explainSection]
      }
      if (relatedPromptsSection) {
        if (messages.routes[relatedPromptsSection]) {
          return messages.routes[relatedPromptsSection]
        }
      }

      const routeFeatureId = ctx.proxy.$route.meta?.featureId

      if (
        featureNode.id?.toLowerCase().includes(routeFeatureId?.toLowerCase()) ||
        routeFeatureId?.toLowerCase().includes(featureNode.routeName?.toLowerCase())
      ) {
        featureId = routeFeatureId
      }

      if (
        featureNode.id === FeatureIdEnum.PLAY &&
        routeFeatureId === FeatureIdEnum.CONFIGURATION &&
        !featureNode.routeName
      ) {
        return messages.features[FeatureIdEnum.BROWSE]
      }

      const featureMessage = messages.features[featureId]

      if (
        (routeFeatureId === FeatureIdEnum.CONFIGURATION &&
          routeFeatureId === featureNode.id &&
          domain === DomainEnum.BROWSE) ||
        (routeFeatureId === FeatureIdEnum.OVERALL &&
          featureNode.id.toLowerCase().includes(routeFeatureId))
      ) {
        return featureMessage[domain]
      }

      if (featureId === FeatureIdEnum.CONFIGURATION) {
        const attributeName = ctx.proxy.$route.params[ROUTER_CONSTS.params.attribute.name]
        const attributeValue = ctx.proxy.$route.params[ROUTER_CONSTS.params.attribute.value]
        if (attributeValue && domain === DomainEnum.BROWSE) {
          return (
            ArrayUtils.findInHierarchy(attributeValue, attributeValues.value[attributeName] ?? [])
              .map(value => value.name)
              .join(' > ') || attributeValue
          )
        }
        return featureMessage[context.value.domain]
      }
      return featureMessage
    }

    const getBreadcrumb = (featureNode: FeatureNode, domain: DomainEnum): Breadcrumb[] => {
      const breadcrumb = [
        {
          id: featureNode.id,
          label: getRouteLabel(featureNode, domain),
          ...(featureNode.routeName
            ? { routeName: featureNode.routeName }
            : featureNode.id === FeatureIdEnum.INSIGHTS
              ? { routeName: ROUTER_CONSTS.routes[FeatureIdEnum.INSIGHTS][DomainEnum.SEARCH] }
              : {}),
        },
      ]

      if (featureNode.parentId) {
        let parentNode = FeaturesTreeUtils.getFeatureNode(
          featureNode.parentId,
          domain ? featuresTree.value[domain] : featuresTree.value[DomainEnum.GLOBAL],
        )

        if (parentNode) {
          if (
            featureNode.id ===
              ROUTER_CONSTS.routes[FeatureIdEnum.CONFIGURATION][DomainEnum.BROWSE].create ||
            featureNode.id ===
              ROUTER_CONSTS.routes[FeatureIdEnum.CONFIGURATION][DomainEnum.BROWSE].update
          ) {
            parentNode = {
              ...featureNode,
              id: ctx.proxy.$route.meta?.parentRouteName,
              routeName: ctx.proxy.$route.meta?.parentRouteName,
            }
          }
          breadcrumb.unshift(...getBreadcrumb(parentNode, domain))
        }
      }

      return breadcrumb
    }

    const hasHighlightsPermission = computed((): boolean => {
      return FeaturesTreeUtils.hasFeatureNodePermission(
        FeatureIdEnum.HIGHLIGHTS,
        featuresTree.value[DomainEnum.GLOBAL],
      )
    })

    const breadcrumbs = computed((): Breadcrumb[] => {
      const domain = context.value.domain

      const featureNode = getFeatureNode(ctx.proxy.$route, domain)

      return [
        ...(hasHighlightsPermission.value
          ? [
              {
                id: FeatureIdEnum.HIGHLIGHTS,
                label: messages.features.highlights,
                routeName: ROUTER_CONSTS.routes[FeatureIdEnum.HIGHLIGHTS],
              },
            ]
          : []),
        ...(featureNode && featureNode.id !== FeatureIdEnum.HIGHLIGHTS
          ? [...getBreadcrumb(featureNode, domain)]
          : []),
      ].filter(breadcrumb => breadcrumb.label)
    })

    function isHighlightsBreadcrumb(breadcrumbId: Breadcrumb['id']): boolean {
      return breadcrumbId === FeatureIdEnum.HIGHLIGHTS
    }

    function isCurrentRoute(routeName: string): boolean {
      return ctx?.proxy.$route.name === routeName
    }

    const allRoutesLabels = computed(() => {
      const features = CONTAINER.get<FeaturesConfig>(TYPES.FeaturesConfig)
      return extractRouteLabels(features)
    })

    function extractRouteLabels(features: FeaturesConfig | FeatureConfig): string[] {
      const labels: string[] = []

      for (const value of Object.values(features)) {
        if (typeof value === 'string') {
          labels.push(value)
        } else if (typeof value === 'object' && value !== null) {
          labels.push(...extractRouteLabels(value))
        }
      }
      return labels
    }

    function isBreadcrumbClickable(breadcrumb: Breadcrumb): boolean {
      const breadcrumbRouteName = breadcrumb.routeName?.toLowerCase()

      const breadcrumbRouteNameExistsInRouteLabels = allRoutesLabels.value.some(
        routeLabel => routeLabel.toLowerCase() === breadcrumbRouteName,
      )

      return breadcrumbRouteNameExistsInRouteLabels
    }

    return {
      baseClass,
      breadcrumbs,
      isHighlightsBreadcrumb,
      isCurrentRoute,
      isBreadcrumbClickable,
    }
  },
})
